import React, { forwardRef, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  IconButton,
  Modal,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { get, isNil, map } from 'lodash';
import moment from 'moment/moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDesktop, faMobileAlt, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import ChatAttachment from './ChatAttachment';
import { useGetUserInfoQuery } from '../../../../../api/userApi';
import { useDispatch, useSelector } from 'react-redux';
import { selectedUserIdSelector } from '../../../../../selectors/userSelectors';
import { useImportMessageToQDeckMutation } from '../../../../../api/messagesApi';
import { setDataMistakesModalMessage } from '../../../../../actions/chatActions';
import { authSelector } from '../../../../../selectors/mainSelectors';
import HighlightedText from './HighlightedText';

const messageIsUnhandled = (message) => isNil(get(message, 'id'));

const MessageMetadata = ({ message }) => {
  return (
    <>
      {messageIsUnhandled(message) && (
        <Typography
          color='red'
          component='span'
          sx={{
            fontSize: '10px',
            textTransform: 'uppercase'
          }}>
          Message did not get processed
        </Typography>
      )}
      {(() => {
        switch (message.client_type) {
          case 'web':
            return <FontAwesomeIcon icon={faDesktop} title='web' />;
          case 'react native':
            return <FontAwesomeIcon icon={faMobileAlt} title='app' />;
          default:
            return null;
        }
      })()}
      {message.delivered && <FontAwesomeIcon icon={['fa', 'check-double']} />}
      {!message.delivered && <FontAwesomeIcon icon={['fa', 'check']} />}
      {message.bookkeeper && (
        <Typography
          component='span'
          sx={{
            fontSize: '10px',
            ml: 1
          }}>
          {message.bookkeeper}
        </Typography>
      )}
      <Typography
        component='span'
        sx={{
          fontSize: '10px'
        }}>
        {moment(message.time).format('MM-DD-YYYY HH:mm:ss')}
      </Typography>
    </>
  );
};

const Message = forwardRef(({ message, onClickImage, searchQuery, style, ...props }, ref) => {
  const dispatch = useDispatch();

  const selectedUserId = useSelector(selectedUserIdSelector);
  const auth = useSelector(authSelector);
  const [isImportToQDeckModalOpen, setIsImportToQDeckModalOpen] = useState(false);
  const prevImportToQDeckErrorRef = useRef(null);
  const prevImportToQDeckDataRef = useRef(null);

  const { data: userInfo, isLoading: isUserInfoLoading } = useGetUserInfoQuery(selectedUserId);

  const [
    importMessageToQDeck,
    { isLoading: isImportingMessageToQDeck, error: importMessageToQDeckError, data: importMessageToQDeckData }
  ] = useImportMessageToQDeckMutation();

  useEffect(() => {
    const errorChanged = importMessageToQDeckError && importMessageToQDeckError !== prevImportToQDeckErrorRef.current;
    const dataChanged = importMessageToQDeckData && importMessageToQDeckData !== prevImportToQDeckDataRef.current;

    if (errorChanged || dataChanged) {
      setIsImportToQDeckModalOpen(true);
      prevImportToQDeckErrorRef.current = importMessageToQDeckError;
      prevImportToQDeckDataRef.current = importMessageToQDeckData;
    }
  }, [importMessageToQDeckError, importMessageToQDeckData]);

  if (isUserInfoLoading) {
    return null;
  }

  const getUserDisplayName = (user) => {
    if (user?.firstname && user?.lastname) {
      return `${user.firstname} ${user.lastname}`;
    }
    if (user?.firstname) {
      return user.firstname;
    }
    return user.email;
  };

  const handleImportMessageToQDeck = async (message) => {
    if (!['user', 'manual'].includes(message.textType)) {
      throw new Error('Only user or agent messages can be imported to QDeck!');
    }

    const authorEmail = message.textType === 'user' ? userInfo.email : auth.user.email;
    const authorName = message.textType === 'user' ? getUserDisplayName(userInfo) : getUserDisplayName(auth.user);
    const authorType = message.textType === 'user' ? 'user' : 'bookkeeper';

    importMessageToQDeck({
      userId: selectedUserId,
      authorEmail,
      authorName,
      authorType,
      text: message.body,
      timestamp: message.time
    });
  };

  const canReportDataMistake = (message) => {
    return messageIsUnhandled(message) || !['user', 'manual', undefined].includes(message.textType);
  };

  return (
    <>
      <Box
        key={`msg${message.id}`}
        ref={ref}
        style={style}
        {...props}
        sx={[
          {
            width: 'calc(100% - 16px)',
            py: 0.5,
            px: 1,
            position: 'relative'
          },
          ...(Array.isArray(props.sx) ? props.sx : [props.sx])
        ]}>
        <Box
          className={`message ${message.className}`}
          sx={{
            border: messageIsUnhandled(message) ? '1px solid red' : null
          }}>
          {message.media === '' && (
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: '1.15',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}>
              <HighlightedText text={message.body} highlight={searchQuery} />
            </Typography>
          )}
          {map(message?.attachments, (attachment, i) => (
            <ChatAttachment attachment={attachment} userId={selectedUserId} key={i} searchQuery={searchQuery} />
          ))}
          {message.media !== '' && (
            <>
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: '1.15',
                  whiteSpace: 'pre-wrap',
                  width: '100%',
                  p: 1,
                  m: 1,
                  wordWrap: 'break-word',
                  float: 'left'
                }}>
                <HighlightedText text={message.body} highlight={searchQuery} />
              </Typography>
              <div className='sms-media'>
                <img src={message.media} alt='sms' onClick={() => onClickImage(message.media)} />
              </div>
            </>
          )}
          <Accordion
            sx={{
              background: 'transparent',
              boxShadow: 'none'
            }}>
            <AccordionSummary
              expandIcon={<FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 16 }} />}
              sx={{
                mt: 0.5,
                minHeight: '36px',
                '&.Mui-expanded': {
                  minHeight: '36px'
                },
                '& .MuiAccordionSummary-content': {
                  margin: 0
                }
              }}>
              <Stack
                direction='row'
                sx={{
                  alignItems: 'center',
                  gap: 1.25,
                  ml: 'auto',
                  mr: 1,
                  fontSize: '10px'
                }}>
                {(message.textType === 'user' || message.textType === 'manual') && (
                  <Tooltip
                    title={
                      isImportingMessageToQDeck ? 'Message is being imported to QDeck...' : 'Import Message into QDeck'
                    }
                    placement='top'>
                    <Stack
                      direction='row'
                      sx={{
                        alignItems: 'center'
                      }}>
                      <IconButton
                        component='div'
                        disableRipple
                        sx={{
                          p: 0,
                          fontSize: '12px',
                          color: 'inherit',
                          cursor: 'pointer'
                        }}
                        onFocus={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImportMessageToQDeck(message);
                        }}>
                        {isImportingMessageToQDeck ? <CircularProgress size={12} /> : '📩'}
                      </IconButton>
                    </Stack>
                  </Tooltip>
                )}

                <MessageMetadata message={message} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1, py: 0 }}>
              <Box
                sx={{
                  display: 'block',
                  alignItems: 'center',
                  gap: 0.5,
                  ml: 1
                }}>
                {message.textType && (
                  <Stack
                    direction='row'
                    sx={{
                      alignItems: 'center',
                      width: '100%',
                      gap: 1
                    }}>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        minWidth: '70px',
                        textAlign: 'left'
                      }}>
                      Text Type
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '10px'
                      }}>
                      {message.textType}
                    </Typography>
                  </Stack>
                )}
                {message.client_type && (
                  <Stack
                    direction='row'
                    sx={{
                      alignItems: 'center',
                      width: '100%',
                      gap: 1
                    }}>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        minWidth: '70px',
                        textAlign: 'left'
                      }}>
                      Client Type
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '10px'
                      }}>
                      {(() => {
                        switch (message.client_type) {
                          case 'web':
                            return 'web';
                          case 'react native':
                            return 'app';
                          default:
                            return null;
                        }
                      })()}
                    </Typography>
                  </Stack>
                )}
                {message.origin && (
                  <Stack
                    direction='row'
                    sx={{
                      alignItems: 'center',
                      width: '100%',
                      gap: 1
                    }}>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        minWidth: '70px',
                        textAlign: 'left'
                      }}>
                      Origin
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '10px'
                      }}>
                      {message.origin}
                    </Typography>
                  </Stack>
                )}
                {message.location && (
                  <Stack
                    direction='row'
                    sx={{
                      alignItems: 'center',
                      width: '100%',
                      gap: 1
                    }}>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        minWidth: '70px',
                        textAlign: 'left'
                      }}>
                      Location
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '10px'
                      }}>
                      {message.location}
                    </Typography>
                  </Stack>
                )}
                {message.page_summary && (
                  <Stack
                    direction='row'
                    sx={{
                      alignItems: 'center',
                      width: '100%',
                      gap: 1
                    }}>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        minWidth: '70px',
                        textAlign: 'left'
                      }}>
                      Page Summary
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '10px'
                      }}>
                      {message.page_summary}
                    </Typography>
                  </Stack>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
          {canReportDataMistake(message) && (
            <IconButton
              size='small'
              sx={{
                position: 'absolute',
                left: 8,
                bottom: 2
              }}
              onClick={() => dispatch(setDataMistakesModalMessage(message))}>
              🐛
            </IconButton>
          )}
        </Box>
      </Box>
      <Modal open={isImportToQDeckModalOpen} onClose={() => setIsImportToQDeckModalOpen(false)}>
        <Paper sx={{ p: 3, maxWidth: 350, overflow: 'auto', margin: 'auto', marginTop: '10vh' }}>
          <Typography>
            {importMessageToQDeckError ? importMessageToQDeckError?.data?.msg : importMessageToQDeckData?.msg}
          </Typography>
        </Paper>
      </Modal>
    </>
  );
});
Message.displayName = 'Message';

const VirtualizedMessages = forwardRef(({ virtualizer, items, onClickImage, searchQuery }, ref) => {
  const virtualItems = virtualizer.getVirtualItems();
  return (
    <Box
      sx={{
        flex: 1,
        userSelect: 'text',
        overflowY: 'auto',
        width: '100%',
        height: '100%'
      }}
      ref={ref}>
      <div
        className='messages-items'
        style={{
          height: virtualizer.getTotalSize(),
          position: 'relative'
        }}>
        {virtualItems.map((virtualRow) => {
          const message = items[virtualRow.index];

          const style = {
            position: 'absolute',
            top: virtualRow.start
          };

          return (
            <Message
              key={virtualRow.index}
              message={message}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={style}
              onClickImage={onClickImage}
              searchQuery={searchQuery}
            />
          );
        })}
      </div>
    </Box>
  );
});
VirtualizedMessages.displayName = 'VirtualizedMessages';

export default VirtualizedMessages;
